import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ensureValidToken, isOfflineMode } from "../utils/authService";
import "../styles/Notification.css";

const Notification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  useEffect(() => {
    const fetchNotifications = async () => {
      // Skip API calls when offline
      if (isOfflineMode()) {
        setLoading(false);
        return;
      }

      try {
        const accessToken = await ensureValidToken();
        if (!accessToken) {
          navigate("/login");
          return;
        }
        const response = await axios.get(
          "https://api.nexcard.co.in/api/cards/request/invitations/",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        
        const formattedNotifications = response.data.data.map(item => ({
          id: item.id,
          title: "Card Request Received",
          description: `${item.requested_by.display_name} has requested your business card.`,
          read: false,
          time: "Recently", // You can format this properly with date-fns or similar
          type: "request",
          is_accepted: item.is_accepted,
          cardId: item.card.id,
          requestedBy: item.requested_by
        }));
        
        setNotifications(formattedNotifications);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotifications();
  }, [navigate]);

  const handleAcceptRequest = async (notificationId, cardId) => {
    if (isOfflineMode()) {
      alert("You are offline. Accepting requests is disabled in offline mode.");
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      await axios.patch(
        `https://api.nexcard.co.in/api/cards/request/${notificationId}/`,
        { is_accepted: true },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Update the notification locally
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, is_accepted: true } : n
      ));
      
      alert("Request accepted successfully!");
    } catch (error) {
      console.error("Error accepting request:", error);
      alert("Failed to accept request. Please try again.");
    }
  };

  const handleDenyRequest = async (notificationId) => {
    if (isOfflineMode()) {
      alert("You are offline. Denying requests is disabled in offline mode.");
      return;
    }

    try {
      const accessToken = await ensureValidToken();
      if (!accessToken) {
        navigate("/login");
        return;
      }
      await axios.delete(
        `https://api.nexcard.co.in/api/cards/request/${notificationId}/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      
      // Remove the notification locally
      setNotifications(notifications.filter(n => n.id !== notificationId));
      alert("Request denied successfully!");
    } catch (error) {
      console.error("Error denying request:", error);
      alert("Failed to deny request. Please try again.");
    }
  };

  const markAllAsRead = () => {
    setNotifications(notifications.map((notification) => ({ ...notification, read: true })));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const filteredNotifications =
    activeTab === "unread"
      ? notifications.filter((notification) => !notification.read)
      : notifications;

  if (loading) {
    return <div className="notification-page">Loading notifications...</div>;
  }

  return (
    <div className="notification-page">
      <button className="back-btn3" onClick={() => navigate(-1)}>
        ← go back
      </button>

      <h2 className="notification-title">Notifications</h2>

      <div className="tabs">
        <button
          className={activeTab === "all" ? "active" : ""}
          onClick={() => setActiveTab("all")}
        >
          All Notifications
        </button>
        <button
          className={activeTab === "unread" ? "active" : ""}
          onClick={() => setActiveTab("unread")}
        >
          Unread
        </button>
      </div>

      <div className="notification-controls">
        <button onClick={markAllAsRead} disabled={notifications.every((n) => n.read)}>
          Mark All as Read
        </button>
        <button onClick={clearAll} disabled={notifications.length === 0}>
          Clear All
        </button>
      </div>

      <div className="notification-list">
        {filteredNotifications.length === 0 ? (
          <p className="empty-message">No notifications available.</p>
        ) : (
          filteredNotifications.map((notification) => (
            <div
              key={notification.id}
              className={`notification-item ${notification.read ? "read" : "unread"}`}
            >
              <div className="notification-content">
                <h4>{notification.title}</h4>
                <p>{notification.description}</p>
                <span className="notification-time">{notification.time}</span>
                {notification.is_accepted && (
                  <span className="status-badge">Accepted</span>
                )}
              </div>
              <div className="notification-actions">
                {notification.type === "request" && !notification.is_accepted && (
                  <>
                    <button onClick={() => handleAcceptRequest(notification.id, notification.cardId)}>
                      Accept
                    </button>
                    <button onClick={() => handleDenyRequest(notification.id)}>
                      Deny
                    </button>
                  </>
                )}
                <button onClick={() => handleDenyRequest(notification.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Notification;